import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DashboardLayout } from "../../components/layout/DashboardLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { DatePicker } from "../../components/ui/date-picker";
import {
  tasksAPI,
  usersAPI,
  clientsAPI,
  entitiesAPI,
  breachReportsAPI,
  dpiaAPI,
  dsrV2API,
  ropaAPI,
  maturityAPI,
  vendorV2API,
} from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { Plus, Search, Trash2, MoreHorizontal, CheckCircle2, Clock, X, ChevronLeft, ChevronRight, Pencil } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { toast } from "sonner";
import { format, differenceInDays } from "date-fns";

const initialFormData = {
  client_id: "",
  entity_id: "",     // null/empty = client-wide; entity admins are auto-tagged on submit
  title: "",
  description: "",
  status: "pending",
  due_date: "",
  assigned_to: "",
  source_module: "",
  source_id: "",
  origin: "dpo",
};

const SOURCE_MODULE_ROUTES = {
  breach: (id) => `/compliance/breaches/workflow/${id}`,
  dpia: (id) => `/risk/dpia/${id}`,
  dsr: (id) => `/compliance/dsr/workflow/${id}`,
  ropa: (id) => `/compliance/ropa/view/${id}`,
  assessment: () => `/compliance/maturity`,
  contract: () => `/compliance/vendors`,
};

export default function TasksPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isDPO, user, isReadOnly, isComplianceReadOnly, isClientAdmin, isEntityAdmin, isEntityViewer } = useAuth();
  // `isComplianceReadOnly` covers both viewers and super_admin — use it on every
  // write affordance throughout the compliance UI.
  const cannotWrite = isComplianceReadOnly;
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEntity] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [editingId, setEditingId] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [summary, setSummary] = useState({ total_open: 0, overdue: 0, due_this_week: 0, completed_last_90_days: 0 });
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [commentBody, setCommentBody] = useState("");
  const [moduleFilter, setModuleFilter] = useState("");
  const [orgFilter, setOrgFilter] = useState("");
  const [entityFilter, setEntityFilter] = useState("");        // "" all, "client_wide" only nulls, or specific entity id
  const [assignedToMe, setAssignedToMe] = useState(false);
  const [users, setUsers] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [linkedRecords, setLinkedRecords] = useState([]);
  const [linkedRecordsLoading, setLinkedRecordsLoading] = useState(false);
  const [formEntities, setFormEntities] = useState([]);        // entities for the form's selected client
  const [filterEntities, setFilterEntities] = useState([]);    // entities for the filter bar's orgFilter
  const formRef = useRef(null);

  // Spec: Entity Admins / Entity Viewers can only ever target their own entity
  const isEntityScoped = isEntityAdmin || isEntityViewer;
  // Client Admins / Client Viewers can pick any entity in their client (or leave client-wide)
  const isClientScoped = isClientAdmin || (user?.role === "client_viewer" || user?.role === "client_view");

  useEffect(() => {
    fetchData();
    fetchOrganisations();
  }, [selectedEntity, statusFilter, moduleFilter]);

  // Spec Section 6: pre-fill form when navigated from source module's "Create task" button
  useEffect(() => {
    const sourceModule = searchParams.get("source_module");
    const sourceId = searchParams.get("source_id");
    const clientIdParam = searchParams.get("client_id");
    const entityIdParam = searchParams.get("entity_id");
    const taskIdParam = searchParams.get("task");
    if (sourceModule || sourceId || clientIdParam || entityIdParam) {
      setFormData((prev) => ({
        ...prev,
        source_module: sourceModule || prev.source_module,
        source_id: sourceId || prev.source_id,
        client_id: clientIdParam || prev.client_id,
        entity_id: entityIdParam || prev.entity_id,
      }));
      setShowForm(true);
      setSearchParams({}, { replace: true });
    } else if (taskIdParam) {
      // Deep-link from notification email — open the task detail panel
      tasksAPI
        .getOne(taskIdParam)
        .then((res) => {
          if (res?.data) openPanel(res.data);
        })
        .catch(() => toast.error("Task not found"))
        .finally(() => setSearchParams({}, { replace: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initial load: fetch DPO users (for Assigned to dropdown) and all portfolio users (for table display names)
  useEffect(() => {
    fetchAllUsers();
    fetchDpoOnlyUsers();
  }, []);

  // Fetch linked records whenever the selected source module changes
  useEffect(() => {
    if (formData.source_module) {
      fetchLinkedRecords(formData.source_module);
    } else {
      setLinkedRecords([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.source_module]);

  const fetchAllUsers = async () => {
    try {
      const res = await usersAPI.getAll();
      setUsers(res.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const fetchDpoOnlyUsers = async () => {
    try {
      // Assigned to is ALWAYS DPO users only — organisation does not scope this dropdown
      const res = await usersAPI.getAll(undefined, undefined, true);
      setFilteredUsers(res.data);
    } catch (error) {
      console.error("Failed to fetch DPO users:", error);
      setFilteredUsers([]);
    }
  };

  const fetchOrganisations = async () => {
    try {
      const response = await clientsAPI.getAll();
      setOrganisations(response.data || response);
    } catch (error) {
      console.error("Failed to fetch organisations:", error);
      setOrganisations([]);
    }
  };

  // Fetch entities for the form's selected client (powers the form's Entity dropdown)
  useEffect(() => {
    const cid = formData.client_id;
    if (!cid) {
      setFormEntities([]);
      return;
    }
    entitiesAPI
      .getAll(cid)
      .then((res) => setFormEntities(res.data || []))
      .catch(() => setFormEntities([]));
  }, [formData.client_id]);

  // Fetch entities for the filter bar's selected org (powers the Entity filter pill)
  useEffect(() => {
    const cid = orgFilter || (isClientScoped || isEntityScoped ? user?.client_id : "");
    if (!cid) {
      setFilterEntities([]);
      return;
    }
    entitiesAPI
      .getAll(cid)
      .then((res) => setFilterEntities(res.data || []))
      .catch(() => setFilterEntities([]));
  }, [orgFilter, user?.client_id, isClientScoped, isEntityScoped]);

  // Reset entity filter when org filter changes (would otherwise reference a stale entity)
  useEffect(() => {
    setEntityFilter("");
  }, [orgFilter]);

  // Resolve entity_id → display name (uses formEntities cache; falls back to enriched task.entity_name)
  const getEntityLabel = (task) => {
    if (!task.entity_id) return "Client-wide";
    if (task.entity_name) return task.entity_name;
    const found = formEntities.find((e) => e.id === task.entity_id) || filterEntities.find((e) => e.id === task.entity_id);
    return found?.entity_name || `#${task.entity_id.slice(0, 6)}`;
  };

  // Per-module record fetcher + display-name resolver for the "Link to Compliance Record" dropdown.
  const LINKED_RECORD_ADAPTERS = {
    breach: {
      fetch: () => breachReportsAPI.getAll(),
      label: (r) =>
        r.breach_reference
          ? `${r.breach_reference} — ${r.title || "Untitled"}`
          : r.title || `Breach #${(r.id || "").slice(0, 8)}`,
    },
    dpia: {
      fetch: () => dpiaAPI.getAll(),
      label: (r) =>
        r.dpia_reference
          ? `${r.dpia_reference} — ${r.project_title || "Untitled"}`
          : r.project_title || `DPIA #${(r.id || "").slice(0, 8)}`,
    },
    dsr: {
      fetch: () => dsrV2API.getAll(),
      label: (r) => {
        const requester = r.requester?.name || "Unknown requester";
        const type = r.request_type || "request";
        return `${type} — ${requester}`;
      },
    },
    ropa: {
      fetch: () => ropaAPI.getAll(),
      label: (r) => r.process_name || `ROPA #${(r.id || "").slice(0, 8)}`,
    },
    assessment: {
      // Option B: fetch all accessible snapshots with entity name + date label
      fetch: () => maturityAPI.getAllSnapshots(),
      label: (r) => r.label || `Snapshot #${(r.id || "").slice(0, 8)}`,
    },
    contract: {
      fetch: () => vendorV2API.getAll(),
      label: (r) => r.vendor_name || r.name || `Vendor #${(r.id || "").slice(0, 8)}`,
    },
  };

  const fetchLinkedRecords = async (sourceModule) => {
    const adapter = LINKED_RECORD_ADAPTERS[sourceModule];
    if (!adapter || !adapter.fetch) {
      setLinkedRecords([]);
      return;
    }
    try {
      setLinkedRecordsLoading(true);
      const res = await adapter.fetch();
      const rows = res?.data || res || [];
      setLinkedRecords(
        rows.map((r) => ({ id: r.id, label: adapter.label(r) }))
      );
    } catch (err) {
      console.error(`Failed to fetch ${sourceModule} records:`, err);
      setLinkedRecords([]);
    } finally {
      setLinkedRecordsLoading(false);
    }
  };


  // Helper function to get user display name from ID
  const getUserDisplayName = (userId) => {
    if (!userId) return "-";
    if (users.length === 0) return "…"; // still loading
    const user = users.find((u) => u.id === userId);
    if (!user) return "Unknown user";
    return user.full_name || user.email || "Unknown user";
  };

  // Role-based 2-letter avatar abbreviation per spec (DA = DPO Admin, CA = Client Admin, EA = Entity Admin)
  const ROLE_ABBR = {
    dpo: "DA",
    super_admin: "SA",
    client_admin: "CA",
    client_viewer: "CV",
    entity_admin: "EA",
    entity_viewer: "EV",
  };

  const getUserRoleAvatar = (userId) => {
    if (!userId) return "?";
    const user = users.find((u) => u.id === userId);
    if (!user) return "?";
    return ROLE_ABBR[user.role] || (user.full_name || "?").substring(0, 2).toUpperCase();
  };

  // Avatar colour + initials for an activity feed item (matches mockup: DA blue, CA/CF green, etc.)
  const getActivityAvatar = (item) => {
    const user = users.find((u) => u.id === item.author_id);
    const role = user?.role;
    const initials =
      (role && ROLE_ABBR[role]) ||
      (item.author_name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase() ||
      "?";

    // Colour by role: DPO = blue, Client = green, Entity = amber, Viewer = slate
    if (role === "dpo" || role === "super_admin") {
      return { initials, bg: "bg-blue-100 dark:bg-blue-950/40", text: "text-blue-700 dark:text-blue-300" };
    }
    if (role === "client_admin" || role === "client_viewer") {
      return { initials, bg: "bg-green-100 dark:bg-green-950/40", text: "text-green-700 dark:text-green-300" };
    }
    if (role === "entity_admin" || role === "entity_viewer") {
      return { initials, bg: "bg-amber-100 dark:bg-amber-950/40", text: "text-amber-700 dark:text-amber-300" };
    }
    return { initials, bg: "bg-slate-200 dark:bg-slate-700", text: "text-slate-700 dark:text-slate-300" };
  };

  // Helper function to get organisation/client name from ID
  const getOrganisationName = (clientId) => {
    if (!clientId) return "Internal";
    if (organisations.length === 0) return "…"; // still loading
    const org = organisations.find((o) => o.id === clientId);
    return org ? org.client_name : "Unknown organisation";
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tasksRes, summaryRes] = await Promise.all([
        tasksAPI.getAll(
          selectedEntity || undefined,
          statusFilter || undefined,
          moduleFilter || undefined,
          searchTerm || undefined,
        ),
        tasksAPI.getSummary(),
      ]);
      setTasks(tasksRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error("Failed to fetch tasks");
    } finally {
      setLoading(false);
    }
  };

  const fetchActivity = async (taskId) => {
    try {
      setActivityLoading(true);
      const res = await tasksAPI.getActivity(taskId);
      setActivity(res.data);
    } catch (error) {
      toast.error("Failed to fetch activity");
    } finally {
      setActivityLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Resolve scope client-side, but the backend re-enforces it server-side too
      const resolvedClient = isEntityScoped || isClientScoped
        ? user?.client_id || null
        : (formData.client_id || null);
      const resolvedEntity = isEntityScoped
        ? user?.entity_id || null
        : (formData.entity_id || null);

      const submitData = {
        ...formData,
        client_id: resolvedClient,
        entity_id: resolvedEntity,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
      };

      if (editingId) {
        await tasksAPI.update(editingId, submitData);
        toast.success("Task updated");
      } else {
        await tasksAPI.create(submitData);
        toast.success("Task created");
      }
      setShowForm(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleInlineStatusUpdate = async (taskId, newStatus) => {
    try {
      await tasksAPI.updateStatus(taskId, newStatus);
      toast.success("Status updated");
      setTasks(tasks.map((t) => (t.id === taskId ? { ...t, status: newStatus } : t)));
      if (selectedTask?.id === taskId) {
        setSelectedTask({ ...selectedTask, status: newStatus });
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleAddComment = async () => {
    if (!commentBody.trim() || !selectedTask) return;
    try {
      await tasksAPI.addComment(selectedTask.id, { body: commentBody });
      toast.success("Comment added");
      setCommentBody("");
      await fetchActivity(selectedTask.id);
    } catch (error) {
      toast.error("Failed to add comment");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await tasksAPI.delete(id);
      toast.success("Task deleted");
      if (selectedTask?.id === id) {
        setPanelOpen(false);
        setSelectedTask(null);
      }
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const resetForm = () => {
    setFormData({
      ...initialFormData,
      // Pre-fill scope from user profile so non-DPO users don't accidentally submit
      // outside their scope (backend would 403 anyway)
      client_id: isClientScoped || isEntityScoped ? (user?.client_id || "") : "",
      entity_id: isEntityScoped ? (user?.entity_id || "") : "",
    });
    setEditingId(null);
  };

  const handleEdit = (task) => {
    setEditingId(task.id);
    setFormData({
      client_id: task.client_id || "",
      entity_id: task.entity_id || "",
      title: task.title || "",
      description: task.description || "",
      status: task.status || "pending",
      due_date: task.due_date ? task.due_date.split("T")[0] : "",
      assigned_to: task.assigned_to || "",
      source_module: task.source_module || "",
      source_id: task.source_id || "",
      origin: task.origin || "dpo",
    });
    setShowForm(true);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const openPanel = async (task) => {
    setSelectedTask(task);
    setPanelOpen(true);
    await fetchActivity(task.id);
  };

  const filteredTasks = tasks.filter((t) => {
    if (searchTerm && !t.title.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (orgFilter && t.client_id !== orgFilter) return false;
    if (entityFilter === "client_wide" && t.entity_id) return false;
    if (entityFilter && entityFilter !== "client_wide" && t.entity_id !== entityFilter) return false;
    if (assignedToMe && t.assigned_to !== user?.id) return false;
    return true;
  });
  const totalCount = filteredTasks.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const pagedTasks = filteredTasks.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);

  // Reset to page 0 when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm, statusFilter, moduleFilter, orgFilter, entityFilter, assignedToMe, itemsPerPage]);

  const navigateToSource = (sourceModule, sourceId) => {
    if (!sourceModule) return;
    const builder = SOURCE_MODULE_ROUTES[sourceModule];
    if (!builder) {
      toast.error(`No route mapped for module: ${sourceModule}`);
      return;
    }
    navigate(builder(sourceId));
  };

  const getDaysRemaining = (dueDate) => {
    if (!dueDate) return null;
    return differenceInDays(new Date(dueDate), new Date());
  };

  const isOverdue = (task) => {
    if (!task.due_date) return false;
    const daysLeft = getDaysRemaining(task.due_date);
    return daysLeft < 0 && task.status !== "completed";
  };

  const SummaryCard = ({ label, value, color = "text-foreground" }) => (
    <Card>
      <CardContent className="p-6">
        <p className="text-sm text-muted-foreground mb-2">{label}</p>
        <p className={`text-3xl font-bold ${color}`}>{value}</p>
      </CardContent>
    </Card>
  );

  const renderTaskDetailPanel = () => {
    if (!selectedTask) return null;

    return (
      <div className="flex flex-col h-full bg-background dark:bg-slate-950 border-l border-border dark:border-slate-800">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border dark:border-slate-800">
          <h2 className="font-semibold text-lg text-foreground">{selectedTask.title}</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              setPanelOpen(false);
              setSelectedTask(null);
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Meta */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Status & Origin Badges */}
            <div className="flex gap-2">
              <Badge
                variant={
                  selectedTask.status === "completed"
                    ? "success"
                    : selectedTask.status === "in_progress"
                      ? "warning"
                      : "secondary"
                }
              >
                {selectedTask.status.replace("_", " ")}
              </Badge>
              {selectedTask.origin && <Badge variant="outline">{selectedTask.origin}</Badge>}
            </div>

            {/* Meta Grid - 2 columns */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-xs">
              {/* Row 1: Organisation & Entity */}
              <div>
                <p className="text-muted-foreground font-semibold uppercase tracking-wide">Organisation</p>
                <p className="mt-1 text-foreground font-medium">{getOrganisationName(selectedTask.client_id)}</p>
              </div>
              <div>
                <p className="text-muted-foreground font-semibold uppercase tracking-wide">Entity</p>
                <p className="mt-1 text-foreground font-medium">
                  {selectedTask.entity_id ? (
                    getEntityLabel(selectedTask)
                  ) : selectedTask.client_id ? (
                    <span className="italic text-muted-foreground">Client-wide</span>
                  ) : (
                    <span className="italic text-muted-foreground">DPO level</span>
                  )}
                </p>
              </div>

              {/* Row: Assigned to */}
              <div>
                <p className="text-muted-foreground font-semibold uppercase tracking-wide">Assigned to</p>
                <p className="mt-1 text-foreground font-medium">
                  {selectedTask.assigned_to ? getUserDisplayName(selectedTask.assigned_to) : "-"}
                </p>
              </div>
              <div></div>

              {/* Row: Due date & Origin */}
              <div>
                <p className="text-muted-foreground font-semibold uppercase tracking-wide">Due date</p>
                <p
                  className={`mt-1 font-medium ${isOverdue(selectedTask) ? "text-red-600 dark:text-red-400" : "text-foreground"}`}
                >
                  {selectedTask.due_date ? format(new Date(selectedTask.due_date), "MMM d, yyyy") : "-"}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground font-semibold uppercase tracking-wide">Origin</p>
                <p className="mt-1 text-foreground font-medium">
                  {selectedTask.origin
                    ? selectedTask.origin.charAt(0).toUpperCase() + selectedTask.origin.slice(1)
                    : "-"}
                </p>
              </div>

              {/* Row 3: Created by & Status */}
              <div>
                <p className="text-muted-foreground font-semibold uppercase tracking-wide">Created by</p>
                <p className="mt-1 text-foreground font-medium">{getUserDisplayName(selectedTask.created_by)}</p>
              </div>
              <div>
                <p className="text-muted-foreground font-semibold uppercase tracking-wide">Status</p>
                <p className="mt-1 text-foreground font-medium">{selectedTask.status.replace("_", " ")}</p>
              </div>
            </div>

            {/* Linked Compliance Objects */}
            {selectedTask.source_module && (
              <div>
                <p className="text-muted-foreground font-semibold uppercase tracking-wide text-xs mb-3">
                  Linked Compliance Objects
                </p>
                <div className="space-y-2">
                  <div
                    onClick={() => navigateToSource(selectedTask.source_module, selectedTask.source_id)}
                    className="p-3 bg-slate-100 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="h-6 w-6 rounded bg-blue-200 dark:bg-blue-900 flex items-center justify-center text-xs font-semibold text-blue-700 dark:text-blue-300 flex-shrink-0 mt-0.5">
                        {selectedTask.source_module.substring(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">
                          {selectedTask.source_module} {selectedTask.source_id && `#${selectedTask.source_id.slice(0, 8)}`}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Description */}
            {selectedTask.description && (
              <div>
                <p className="text-muted-foreground font-medium text-sm">Notes</p>
                <p className="mt-2 text-sm text-foreground">{selectedTask.description}</p>
              </div>
            )}

            {/* Comments Section */}
            <div className="border-t border-border dark:border-slate-800 pt-6">
              <h3 className="font-semibold text-sm mb-4 text-foreground">Activity</h3>

              {activityLoading ? (
                <p className="text-sm text-muted-foreground">Loading...</p>
              ) : activity.length === 0 ? (
                <p className="text-sm text-muted-foreground">No activity yet</p>
              ) : (
                <div className="space-y-5">
                  {activity.map((item, idx) => {
                    const { bg, text, initials } = getActivityAvatar(item);
                    return (
                      <div key={idx} className="flex items-start gap-3 text-sm">
                        <div
                          className={`h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0 ${bg} ${text}`}
                        >
                          {initials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-foreground leading-snug">{item.body}</p>
                          {item.created_at && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {format(new Date(item.created_at), "MMM d, yyyy")} · {item.author_name}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Comment Input */}
        {!cannotWrite && (
          <div className="border-t border-border dark:border-slate-800 p-4 bg-background dark:bg-slate-950">
            <div className="flex gap-3">
              <Textarea
                placeholder="Add a note or update..."
                value={commentBody}
                onChange={(e) => setCommentBody(e.target.value)}
                className="min-h-[44px] resize-none bg-background dark:bg-slate-900 border-border dark:border-slate-800 text-sm"
              />
              <Button
                onClick={handleAddComment}
                size="sm"
                disabled={!commentBody.trim()}
                className="flex-shrink-0 h-11 px-6"
              >
                Send
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Tasks</h1>
            <p className="text-muted-foreground">Manage compliance tasks and activities</p>
          </div>
          {!cannotWrite && (
            <Button
              onClick={() => {
                resetForm();
                setShowForm(true);
                setTimeout(() => {
                  formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 100);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              New task
            </Button>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard label="Total Open" value={summary.total_open} />
          <SummaryCard label="Overdue" value={summary.overdue} color="text-red-600" />
          <SummaryCard label="Due This Week" value={summary.due_this_week} color="text-amber-600" />
          <SummaryCard label="Completed (90d)" value={summary.completed_last_90_days} color="text-green-600" />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Status pill: cycles All → Pending → In Progress → Blocked → Complete */}
          <Select
            value={statusFilter && statusFilter !== "overdue" ? statusFilter : "all"}
            onValueChange={(val) => setStatusFilter(val === "all" ? "" : val)}
          >
            <SelectTrigger
              className={`h-9 w-auto rounded-full px-4 gap-2 ${
                statusFilter && statusFilter !== "overdue"
                  ? "border-primary text-primary"
                  : ""
              }`}
            >
              <SelectValue placeholder="All status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>

          {/* Overdue quick-filter pill */}
          <Button
            type="button"
            variant={statusFilter === "overdue" ? "default" : "outline"}
            size="sm"
            className="h-9 rounded-full px-4"
            onClick={() => setStatusFilter(statusFilter === "overdue" ? "" : "overdue")}
          >
            Overdue
          </Button>

          {/* Organisation filter (DPO/super_admin only — others see only their own client) */}
          {isDPO && (
            <Select
              value={orgFilter || "all"}
              onValueChange={(val) => setOrgFilter(val === "all" ? "" : val)}
            >
              <SelectTrigger
                className={`h-9 w-auto rounded-full px-4 gap-2 ${orgFilter ? "border-primary text-primary" : ""}`}
              >
                <SelectValue placeholder="All orgs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All orgs</SelectItem>
                {organisations.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.client_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Entity filter — hidden for entity-level roles (already locked to their entity) */}
          {!isEntityScoped && filterEntities.length > 0 && (
            <Select
              value={entityFilter || "all"}
              onValueChange={(val) => setEntityFilter(val === "all" ? "" : val)}
            >
              <SelectTrigger
                className={`h-9 w-auto rounded-full px-4 gap-2 ${entityFilter ? "border-primary text-primary" : ""}`}
              >
                <SelectValue placeholder="All entities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All entities</SelectItem>
                <SelectItem value="client_wide">Client-wide only</SelectItem>
                {filterEntities.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.entity_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Assigned to me quick-filter pill */}
          <Button
            type="button"
            variant={assignedToMe ? "default" : "outline"}
            size="sm"
            className="h-9 rounded-full px-4"
            onClick={() => setAssignedToMe((v) => !v)}
          >
            Assigned to me
          </Button>

          {/* Module filter */}
          <Select
            value={moduleFilter || "all"}
            onValueChange={(val) => setModuleFilter(val === "all" ? "" : val)}
          >
            <SelectTrigger
              className={`h-9 w-auto rounded-full px-4 gap-2 ${moduleFilter ? "border-primary text-primary" : ""}`}
            >
              <SelectValue placeholder="Module" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All modules</SelectItem>
              <SelectItem value="breach">Breach</SelectItem>
              <SelectItem value="dpia">DPIA</SelectItem>
              <SelectItem value="dsr">DSR</SelectItem>
              <SelectItem value="ropa">ROPA</SelectItem>
              <SelectItem value="assessment">Assessment</SelectItem>
              <SelectItem value="contract">Contract</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table + Panel Split */}
        <div className="flex gap-0 relative">
          <div className={`transition-all min-w-0 ${panelOpen ? "w-[60%]" : "w-full"}`}>
            <Card>
              <CardContent className="p-0 overflow-x-auto">
                <Table className={panelOpen ? "min-w-[900px]" : ""}>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Task</TableHead>
                      <TableHead>Organisation</TableHead>
                      <TableHead>Entity</TableHead>
                      <TableHead>Assigned to</TableHead>
                      <TableHead>Due</TableHead>
                      <TableHead>Linked to</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8">
                          Loading...
                        </TableCell>
                      </TableRow>
                    ) : filteredTasks.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="p-0 border-0">
                          <div className="py-12 px-6">
                            <div className="flex flex-col items-center justify-center text-center">
                              <div className="relative mb-6">
                                <div className="absolute inset-0 bg-teal-500/20 rounded-full blur-xl" />
                                <div className="relative flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-teal-500/10 to-teal-600/10 border border-teal-500/20">
                                  <CheckCircle2 className="h-8 w-8 text-teal-600 dark:text-teal-400" />
                                </div>
                              </div>
                              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                                {searchTerm || statusFilter || moduleFilter || orgFilter || assignedToMe ? "No matching tasks" : "All caught up!"}
                              </h3>
                              <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm mb-6">
                                {searchTerm || statusFilter || moduleFilter || orgFilter || assignedToMe
                                  ? "Try adjusting your filters to find what you're looking for."
                                  : "Create tasks to track compliance activities, deadlines, and follow-ups."}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      pagedTasks.map((task) => {
                        const daysRemaining = getDaysRemaining(task.due_date);
                        const isTaskOverdue = isOverdue(task);
                        return (
                          <TableRow
                            key={task.id}
                            onClick={() => openPanel(task)}
                            className={`cursor-pointer hover:bg-muted/50 ${task.status === "completed" ? "opacity-60" : ""}`}
                          >
                            {/* Task: Title + subtitle (origin/source · deadline info) */}
                            <TableCell>
                              <div>
                                <p className={`font-medium ${task.status === "completed" ? "line-through" : ""}`}>
                                  {task.title}
                                </p>
                                {(() => {
                                  const parts = [];
                                  if (task.origin === "auto") {
                                    parts.push(
                                      task.source_module
                                        ? `Auto-generated · ${task.source_module.toUpperCase()} finding`
                                        : "Auto-generated"
                                    );
                                  } else if (task.origin === "client") {
                                    parts.push("Client action required");
                                  } else if (task.origin === "dpo") {
                                    parts.push(task.client_id ? "DPO action" : "DPO managed");
                                  }
                                  if (daysRemaining !== null && task.status !== "completed") {
                                    if (daysRemaining < 0) parts.push(`Overdue by ${Math.abs(daysRemaining)} days`);
                                    else if (daysRemaining === 0) parts.push("Due today");
                                    else if (daysRemaining <= 7) parts.push(`${daysRemaining} days remaining`);
                                  }
                                  return parts.length > 0 ? (
                                    <p
                                      className={`text-xs mt-1 ${
                                        daysRemaining !== null && daysRemaining < 0 && task.status !== "completed"
                                          ? "text-red-500 font-semibold"
                                          : daysRemaining !== null && daysRemaining <= 3 && task.status !== "completed"
                                            ? "text-amber-500 font-semibold"
                                            : "text-muted-foreground"
                                      }`}
                                    >
                                      {parts.join(" · ")}
                                    </p>
                                  ) : null;
                                })()}
                              </div>
                            </TableCell>

                            {/* Organisation */}
                            <TableCell className="text-sm">{getOrganisationName(task.client_id)}</TableCell>

                            {/* Entity column.
                                - entity_id set                       → entity name
                                - client_id set, entity_id null       → "Client-wide" (spans all entities under that client)
                                - client_id null, entity_id null      → "DPO level" (pure DPO-internal record, no client) */}
                            <TableCell className="text-sm">
                              {task.entity_id ? (
                                <span>{getEntityLabel(task)}</span>
                              ) : task.client_id ? (
                                <span className="text-muted-foreground italic">Client-wide</span>
                              ) : (
                                <span className="text-muted-foreground italic">DPO level</span>
                              )}
                            </TableCell>

                            {/* Assigned to: Avatar + name/role */}
                            <TableCell className="text-sm">
                              {task.assigned_to ? (
                                <div className="flex items-center gap-2">
                                  <div className="h-6 w-6 rounded-full bg-slate-300 dark:bg-slate-600 flex items-center justify-center text-[10px] font-semibold">
                                    {getUserRoleAvatar(task.assigned_to)}
                                  </div>
                                  <span>{getUserDisplayName(task.assigned_to)}</span>
                                </div>
                              ) : (
                                "-"
                              )}
                            </TableCell>

                            {/* Due: Red + bold when overdue */}
                            <TableCell>
                              {task.due_date ? (
                                <div className="flex items-center gap-2">
                                  <span className={isTaskOverdue ? "text-red-500 font-bold" : ""}>
                                    {format(new Date(task.due_date), "MMM d")}
                                  </span>
                                  {isTaskOverdue && <Clock className="h-4 w-4 text-red-500" />}
                                </div>
                              ) : (
                                "-"
                              )}
                            </TableCell>

                            {/* Linked to: Chip badge (tappable) */}
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              {task.source_module && (
                                <Badge
                                  variant="secondary"
                                  className="text-xs cursor-pointer hover:opacity-80"
                                  onClick={() => navigateToSource(task.source_module, task.source_id)}
                                >
                                  {task.source_module} {task.source_id && `#${task.source_id.slice(0, 3)}…`}
                                </Badge>
                              )}
                            </TableCell>

                            {/* Status: Pill badge */}
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <Select
                                value={task.status}
                                onValueChange={(s) => handleInlineStatusUpdate(task.id, s)}
                                disabled={cannotWrite}
                              >
                                <SelectTrigger className="h-7 w-[110px] text-xs border-0 shadow-none bg-transparent">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="in_progress">In Progress</SelectItem>
                                  <SelectItem value="blocked">Blocked</SelectItem>
                                  <SelectItem value="completed">Completed</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              {!cannotWrite && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon">
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => handleEdit(task)}>
                                      <Pencil className="h-4 w-4 mr-2" />
                                      Edit
                                    </DropdownMenuItem>
                                    {/* Delete is DPO-only (super_admin is view-only per spec) */}
                                    {user?.role === "dpo" && (
                                      <DropdownMenuItem onClick={() => handleDelete(task.id)} className="text-red-600">
                                        <Trash2 className="h-4 w-4 mr-2" />
                                        Delete
                                      </DropdownMenuItem>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>

                {/* Pagination Controls */}
                {totalCount > 0 && (
                  <div className="flex flex-col gap-4 p-4 mt-4 bg-muted rounded-lg">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div className="text-sm text-muted-foreground">
                        Showing {currentPage * itemsPerPage + 1} to {Math.min((currentPage + 1) * itemsPerPage, totalCount)} of {totalCount} tasks
                      </div>
                      <div className="flex items-center gap-3">
                        <label htmlFor="tasks-items-per-page" className="text-sm text-muted-foreground">Items per page:</label>
                        <Select value={String(itemsPerPage)} onValueChange={(val) => setItemsPerPage(Number(val))}>
                          <SelectTrigger className="w-[80px] h-9" id="tasks-items-per-page">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="10">10</SelectItem>
                            <SelectItem value="25">25</SelectItem>
                            <SelectItem value="50">50</SelectItem>
                            <SelectItem value="100">100</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div className="text-sm font-medium">Page {currentPage + 1} of {totalPages}</div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                          disabled={currentPage === 0}
                        >
                          <ChevronLeft className="h-4 w-4 mr-1" />
                          Previous
                        </Button>

                        <div className="flex gap-1">
                          {Array.from({ length: totalPages }).map((_, idx) => {
                            const pageNum = idx;
                            const isCurrentPage = pageNum === currentPage;
                            const isVisible =
                              Math.abs(pageNum - currentPage) <= 2 ||
                              pageNum === 0 ||
                              pageNum === totalPages - 1;
                            if (!isVisible) {
                              if (pageNum === 1 && currentPage > 3)
                                return <span key="ellipsis-start" className="px-1">...</span>;
                              if (pageNum === totalPages - 2 && currentPage < totalPages - 4)
                                return <span key="ellipsis-end" className="px-1">...</span>;
                              return null;
                            }
                            return (
                              <Button
                                key={pageNum}
                                variant={isCurrentPage ? "default" : "outline"}
                                size="sm"
                                onClick={() => setCurrentPage(pageNum)}
                                className={isCurrentPage ? "min-w-9" : ""}
                              >
                                {pageNum + 1}
                              </Button>
                            );
                          })}
                        </div>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                          disabled={currentPage >= totalPages - 1}
                        >
                          Next
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Panel */}
          <div className={`transition-all border-l bg-background overflow-hidden ${panelOpen ? "w-[40%]" : "w-0"}`}>
            {renderTaskDetailPanel()}
          </div>
        </div>

        {/* Inline Form */}
        {showForm && !cannotWrite && (
          <Card ref={formRef}>
            <CardContent className="p-6">
              <div className="mb-6">
                <h3 className="text-xl font-semibold">{editingId ? "Edit task" : "New task"}</h3>
                <p className="text-sm text-muted-foreground">
                  {editingId ? "Update task details below" : "Create a task and optionally link it to a compliance record"}
                </p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Title */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                    required
                    placeholder="What needs to be done?"
                    className="h-10"
                  />
                </div>

                {/* Organisation & Entity */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Organisation {isDPO ? "*" : ""}</Label>
                    <Select
                      value={formData.client_id || "none"}
                      onValueChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          client_id: value === "none" ? "" : value,
                          entity_id: "",  // reset entity when client changes
                        }))
                      }
                      disabled={!isDPO}  /* Client/Entity admins are locked to their own client */
                    >
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="None (DPO task)" />
                      </SelectTrigger>
                      <SelectContent>
                        {isDPO && <SelectItem value="none">None (DPO task)</SelectItem>}
                        {organisations && organisations.length > 0
                          ? organisations.map((org) => (
                              <SelectItem key={org.id} value={org.id}>
                                {org.client_name}
                              </SelectItem>
                            ))
                          : null}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Entity</Label>
                    <Select
                      value={formData.entity_id || "client_wide"}
                      onValueChange={(value) =>
                        setFormData((prev) => ({ ...prev, entity_id: value === "client_wide" ? "" : value }))
                      }
                      disabled={isEntityScoped || !formData.client_id}
                    >
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder={!formData.client_id ? "Pick organisation first" : "Client-wide (all entities)"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="client_wide">Client-wide (all entities)</SelectItem>
                        {formEntities.map((e) => (
                          <SelectItem key={e.id} value={e.id}>
                            {e.entity_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {isEntityScoped && (
                      <p className="text-xs text-muted-foreground">Auto-tagged to your entity</p>
                    )}
                  </div>
                </div>

                {/* Assigned to (full row) */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Assigned to</Label>
                  <Select
                    value={formData.assigned_to || "none"}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, assigned_to: value === "none" ? "" : value }))
                    }
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Select user" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {filteredUsers &&
                        filteredUsers.length > 0 &&
                        filteredUsers.map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.full_name || u.email} {u.role && `(${u.role})`}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Due Date & Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Due date</Label>
                    <DatePicker
                      value={formData.due_date}
                      onChange={(val) => setFormData((prev) => ({ ...prev, due_date: val }))}
                      placeholder="mm/dd/yyyy"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) => setFormData((prev) => ({ ...prev, status: value }))}
                    >
                      <SelectTrigger className="h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="blocked">Blocked</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Link to Compliance Record */}
                <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h3 className="font-semibold text-sm text-blue-900 dark:text-blue-400 mb-4 uppercase tracking-wide">
                    Link to Compliance Record (Optional)
                  </h3>
                  <div className="grid grid-cols-2 gap-4 items-start">
                    <div className="flex flex-col gap-2">
                      <Label className="text-gray-700 dark:text-gray-300">Module</Label>
                      <Select
                        value={formData.source_module || "__none__"}
                        onValueChange={(value) =>
                          setFormData((prev) => ({
                            ...prev,
                            source_module: value === "__none__" ? "" : value,
                            source_id: "",
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="None" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">None</SelectItem>
                          <SelectItem value="breach">Breach</SelectItem>
                          <SelectItem value="dpia">DPIA</SelectItem>
                          <SelectItem value="dsr">DSR</SelectItem>
                          <SelectItem value="ropa">ROPA</SelectItem>
                          <SelectItem value="assessment">Assessment</SelectItem>
                          <SelectItem value="contract">Contract</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Label className="text-gray-700 dark:text-gray-300">Record</Label>
                      <Select
                          value={formData.source_id || "__none__"}
                          onValueChange={(value) =>
                            setFormData((prev) => ({
                              ...prev,
                              source_id: value === "__none__" ? "" : value,
                            }))
                          }
                          disabled={!formData.source_module || linkedRecordsLoading}
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={
                                !formData.source_module
                                  ? "Select module first"
                                  : linkedRecordsLoading
                                    ? "Loading…"
                                    : linkedRecords.length === 0
                                      ? "No records found"
                                      : "Select a record"
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">None</SelectItem>
                            {linkedRecords.map((r) => (
                              <SelectItem key={r.id} value={r.id}>
                                {r.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Notes</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                    placeholder="Any context or instructions for the assignee..."
                    rows={3}
                  />
                </div>

                {/* Buttons */}
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowForm(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit">{editingId ? "Update task" : "Create task"}</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
